import { useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components/macro';
import { line, area, curveMonotoneX, curveLinear } from 'd3-shape';
import last from 'lodash.last';
import {
  bool,
  func,
  number,
  string,
  oneOf,
  shape,
  instanceOf,
  any,
} from 'prop-types';

import entitiesPropType from './prop-types/entities';
import selectedEntitiesPropType from './prop-types/selectedEntities';
import periodPropType from './prop-types/period';

import getMargins from './helpers/getMargins';
import getEntityKey from 'helpers/getEntityKey';
import generateRandomHash from 'helpers/generateRandomHash';
import { useTimeScale } from '../utils/createTimeScale';
import { useLinearScale } from '../utils/createLinearScale';
import { useBandScale } from '../utils/createBandScale';
import { round } from './helpers/math';

import { isLineChartPathDefined } from 'components/Overview/LineChart/utils/pathDefinitions';

import Axes from './Axes';
import Hitbox from './Hitbox';
import ThisWeek from './ThisWeek';
import Tracker from './Tracker';
import ChartLines from './ChartLines';

export const InnerGroup = styled('g')`
  overflow: visible;
  pointer-events: none;
  user-select: none;
`;

const noop = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function

/**
 * This component, given the correct props, returns a LineChart with one or multiple trend
 * lines, configured axes' and an (optional) interactive tracker.
 *
 * @param {object} props React props
 * @param {function} props.colorScale Configured D3.js color scale
 * @param {function} props.curve D3.js curving method to use
 * @param {('number'|'position'|'social')} props.dataType The type of data displayed
 * @param {array} props.entities Array of entities to visualise
 * @param {boolean} props.fillFromLastNonNullValue Should the trend lines be filled with a dashed line to the last data point?
 * @param {boolean} props.hasNoData Does this linechart have no data?
 * @param {boolean} props.hasComplementary Does this linechart have a complementary linechart?
 * @param {boolean} props.hasDashedGaps Should the linechart display dashed lines to fill gaps (null values) in the trend lines?
 * @param {function} props.handleDragChange Handle touch drag position changes
 * @param {function} props.handleMouseClick Handle mouse clicks
 * @param {function} props.handleMouseHover Handle mouse hovers
 * @param {boolean} props.isComplementary Is this linechart a complementary linechart?
 * @param {boolean} props.isDragging Is a user currently touch dragging over the linechart?
 * @param {boolean} props.isSmallScreen Is this a small screen?
 * @param {string} props.label The vertical axis label
 * @param {string} props.additionalLabel The label above the chart
 * @param {number} props.parentHeight The height of the parent container
 * @param {number} props.parentWidth The width of the parent container
 * @param {('day'|'week'|'month'|'year'|customDate)} props.period The interval of the displayed data
 * @param {boolean} props.shouldTransitionIn Should this linechart transition in now?
 * @param {boolean} props.showVerticalAxisLabels Should the vertical axis label be displayed?
 * @param {boolean} props.showTooltip Should the in-graph tooltip be displayed?
 * @param {array} props.selectedEntities The currently selected entities
 * @param {array} props.temporarySelectedEntities The temporary currently selected entities (only while hovering)
 * @param {object} props.tooltipRoot React ref to a DOM element
 */
function LineChart(props) {
  const {
    colorScale,
    curve = curveMonotoneX,
    dataType = 'number',
    entities,
    fillFromLastNonNullValue,
    hasNoData,
    hasComplementary,
    hasDashedGaps,
    handleDragChange = noop,
    handleMouseClick = noop,
    handleMouseHover = noop,
    isComplementary,
    isDragging,
    isSmallScreen = true,
    isLabelRotated = false,
    label,
    additionalLabel,
    parentHeight = 960,
    parentWidth = 384,
    period = 'day',
    shouldTransitionIn,
    showVerticalAxisLabels = true,
    showTooltip,
    selectedEntities,
    temporarySelectedEntities,
    tooltipRoot,
    isStrictDesign,
    trackerLineStart,
    isCutGrid,
    trends,
    viewType,
    customPeriod,
    setChartMargin,
  } = props;

  /**
   * Instance variables
   */
  const randomHash = useRef(generateRandomHash());
  const clipPathId = useRef(
    `clip-sides-${getEntityKey(entities?.[0])}-${randomHash.current}`
  );
  const trackerTextBGFilterId = useRef(
    `tracker-text-bg-filter-${getEntityKey(entities?.[0])}-${
      randomHash.current
    }`
  );
  /**
   * Local Variables
   */
  const dataKey = dataType === 'number' ? 'value' : dataType;
  const isPositionChart = dataType === 'position';
  const margin = useMemo(
    () => getMargins(hasComplementary, period, isCutGrid),
    [isCutGrid, hasComplementary, period]
  );

  const chartPeriod = useMemo(() => {
    return period === 'customDate' ? customPeriod || 'week' : period;
  }, [period]);

  const width = useMemo(() => {
    // avoid negative widths.
    if (parentWidth === 0) return parentWidth;
    return round(parentWidth - margin.left - margin.right);
  }, [margin, parentWidth]);

  const height = useMemo(() => {
    // avoid negative heights.
    if (parentHeight === 0) return parentHeight;
    return round(parentHeight - margin.top - margin.bottom);
  }, [margin, parentHeight]);

  const numberOfDataPoints = useMemo(
    () => Math.max(...entities.map((entity) => entity.data.length)),
    [entities]
  );

  const showInteractiveElements = Boolean(
    !hasNoData && shouldTransitionIn && handleMouseClick && handleMouseHover
  );

  /**
   * Scales
   */
  const x = useTimeScale(
    entities,
    chartPeriod,
    [0, isStrictDesign && !isCutGrid ? width - 45 : width],
    isCutGrid,
    period === 'customDate'
  );
  const y = useLinearScale(entities, dataKey, [height, 0]);
  const band = useBandScale(x, chartPeriod);

  /** Path generators */
  const path = useCallback(
    line()
      .x((d) => x(d.date))
      .y((d) => y(d[dataKey]))
      .defined((d) => isLineChartPathDefined(d, dataKey))
      .curve(isPositionChart ? curveLinear : curve),
    [x, y, dataKey, isPositionChart]
  );

  const gradient = useCallback(
    area()
      .defined((d) => d[dataKey] !== null)
      .x((d) => x(d.date))
      .y0(() => height)
      .y1((d) => y(d[dataKey]))
      .curve(isPositionChart ? curveLinear : curve),
    [x, y, dataKey, isPositionChart]
  );

  /**
   * Get the y-position of the topmost vertical tick.
   */
  const firstTickY = useMemo(() => {
    if (isPositionChart) return round(y(y.domain()[1]));
    const yTicks = y.ticks(6);
    return round(y(last(yTicks)));
  }, [isPositionChart, y]);

  useEffect(() => {
    if (setChartMargin) {
      setChartMargin(firstTickY === 0 ? 0 : firstTickY - 10);
    }
  }, [firstTickY]);

  return (
    <>
      <defs>
        <clipPath id={clipPathId.current}>
          <rect x={0} y={0} width={width} height={height} />
        </clipPath>

        <filter
          id={trackerTextBGFilterId.current}
          x={-0.3}
          y={0}
          width="1.6"
          height="1.2"
          primitiveUnits="objectBoundingBox"
        >
          <feImage
            preserveAspectRatio="none"
            xlinkHref="data:image/svg+xml;charset=UTF-8,%3csvg width='10' height='1' xmlns='http://www.w3.org/2000/svg'%3e%3cdefs%3e%3clinearGradient id='filterGradient'%3e%3cstop offset='0%25' stop-color='white' stop-opacity='0' /%3e%3cstop offset='15%25' stop-color='white' stop-opacity='1' /%3e%3cstop offset='85%25' stop-color='white' stop-opacity='1' /%3e%3cstop offset='100%25' stop-color='white' stop-opacity='0' /%3e%3c/linearGradient%3e%3c/defs%3e%3crect x='0%25' y='0%25' width='100%25' height='100%25' fill='url(%23filterGradient)'/%3e%3c/svg%3e"
          />
          <feComposite in="SourceGraphic" />
        </filter>
      </defs>

      {chartPeriod === 'day' && (
        <InnerGroup transform={`translate(${margin.left}, 0)`}>
          <ThisWeek
            x={x}
            y={y}
            topMargin={margin.top}
            yPos={firstTickY}
            height={height}
            isSmallScreen={isSmallScreen}
            isComplementary={isComplementary}
          />
        </InnerGroup>
      )}

      <Axes
        x={x}
        y={y}
        trends={entities}
        period={chartPeriod}
        isSmallScreen={isSmallScreen}
        hasNoData={hasNoData}
        sizes={{ width, height }}
        margin={margin}
        dataType={dataType}
        labels={{
          x: null,
          y: label,
        }}
        showVerticalAxisLabels={showVerticalAxisLabels}
        hasComplementary={hasComplementary}
        numberOfDataPoints={numberOfDataPoints}
        additionalLabel={additionalLabel}
        isLabelRotated={isLabelRotated}
        yAxisOffset={isCutGrid && 4}
        isCutGrid={isCutGrid}
      />

      {showInteractiveElements && (
        <Hitbox
          transform={`translate(${margin.left}, 0)`}
          size={{
            width,
            height: parentHeight,
          }}
          period={chartPeriod}
          x={x}
          handleMouseClick={handleMouseClick}
          handleMouseHover={handleMouseHover}
          handleDragChange={handleDragChange}
        />
      )}

      <InnerGroup transform={`translate(${margin.left}, ${margin.top})`}>
        <ChartLines
          trends={entities}
          colorScale={colorScale}
          dataType={dataType}
          dataKey={dataKey}
          fillFromLastNonNullValue={fillFromLastNonNullValue}
          width={width}
          height={height}
          path={path}
          x={x}
          y={y}
          gradient={gradient}
          hasDashedGaps={hasDashedGaps}
        />
      </InnerGroup>

      {showInteractiveElements && (
        <>
          <InnerGroup transform={`translate(${margin.left}, ${margin.top})`}>
            {Boolean(temporarySelectedEntities) && (
              <Tracker
                band={band}
                clipPathId={clipPathId.current}
                colorScale={colorScale}
                dataKey={dataKey}
                hasComplementary={hasComplementary}
                height={height}
                isGhostTracker
                isSmallScreen={isSmallScreen}
                period={chartPeriod}
                selectedEntities={selectedEntities}
                allEntities={trends}
                textBackgroundFilterId={trackerTextBGFilterId.current}
                width={width}
                x={x}
                y={y}
                yStart={firstTickY}
                margin={margin}
                isStrictDesign={isStrictDesign}
                trackerLineStart={trackerLineStart}
                viewType={viewType}
              />
            )}
            <Tracker
              band={band}
              clipPathId={clipPathId.current}
              colorScale={colorScale}
              dataKey={dataKey}
              hasComplementary={hasComplementary}
              height={height}
              isDragging={isDragging}
              isSmallScreen={isSmallScreen}
              label={label}
              margin={margin}
              period={chartPeriod}
              selectedEntities={temporarySelectedEntities || selectedEntities}
              allEntities={trends}
              showTooltip={showTooltip}
              textBackgroundFilterId={trackerTextBGFilterId.current}
              tooltipRoot={tooltipRoot}
              width={width}
              x={x}
              y={y}
              yStart={firstTickY}
              isStrictDesign={isStrictDesign}
              trackerLineStart={trackerLineStart}
              viewType={viewType}
            />
          </InnerGroup>
        </>
      )}
    </>
  );
}

LineChart.propTypes = {
  colorScale: func.isRequired,
  curve: func,
  dataType: oneOf(['position', 'social', 'number']),
  entities: entitiesPropType,
  fillFromLastNonNullValue: bool,
  handleDragChange: func,
  handleMouseClick: func.isRequired,
  handleMouseHover: func.isRequired,
  hasComplementary: bool,
  hasDashedGaps: bool,
  hasNoData: bool,
  isComplementary: bool,
  isDragging: bool,
  isSmallScreen: bool,
  label: string,
  additionalLabel: string,
  parentHeight: number,
  parentWidth: number,
  period: periodPropType.isRequired,
  selectedEntities: selectedEntitiesPropType.isRequired,
  shouldTransitionIn: bool,
  showTooltip: bool,
  showVerticalAxisLabels: bool,
  temporarySelectedEntities: selectedEntitiesPropType,
  tooltipRoot: shape({ current: instanceOf(Element) }),
  isStrictDesign: bool,
  isLabelRotated: bool,
  trackerLineStart: bool,
  isCutGrid: bool,
  trends: any,
  viewType: string,
  customPeriod: string,
};

export default LineChart;
